import {
  isNil,
  get,
  reduce,
  isArray,
  concat,
  isFunction,
  mergeWith,
  merge,
  camelCase,
  upperFirst,
  isEmpty,
  omit,
  compact,
  last,
  keys,
  dropRight,
  isEqual,
} from 'lodash';
import { ContentColumns, ContentText, ContentTable } from 'pdfmake/interfaces';

import {
  IPdfGrid,
  PdfSignature,
  PdfData,
  PdfContent,
  IPdfDefinitionsWithInnerProperty,
  PdfSummary,
  IPdfServices,
  IPdfOptions,
  IPdfTools,
  IPdfDefinitions,
  PdfSimpleText,
  PdfContentSetting,
  PdfContentBase,
  NestedPdfContent,
  INestedPdfContentOptions,
} from '../pdf.type';
import { IPdfContentBuilder } from './pdf-content-builder.type';
import { IPdfData } from '../pdf-data.type';
import { DEFAULT_PDF_OPTIONS } from '../constants';

abstract class AbstractPdfContentBuilder implements IPdfContentBuilder {
  public readonly services: IPdfServices;

  public get defaultOptions(): IPdfOptions {
    return DEFAULT_PDF_OPTIONS;
  }

  public constructor(services: IPdfServices) {
    this.services = services;
  }

  public abstract buildContentSimpleText(
    text: PdfSimpleText,
    data: IPdfData,
    pdfDefinition: IPdfDefinitionsWithInnerProperty,
  ): ContentText[];

  /**
   * build content summary
   *
   * @param summary summary options
   * @param summaryData summary data
   */
  public abstract buildContentSummary(
    summary: PdfSummary,
    summaryData: IPdfData,
    pdfDefinition: IPdfDefinitionsWithInnerProperty,
    contentKey: string,
  ): ContentColumns[];

  /**
   * build content grid tables
   *
   * @param grids grids options
   */
  public abstract buildContentGrids(
    grids: Record<string, IPdfGrid>,
    data: PdfData,
    pdfDefinition: IPdfDefinitionsWithInnerProperty,
    contentKey: string,
  ): (ContentTable | ContentText | PdfContent)[];

  /**
   * build content signatures
   *
   * @param signatures signature options
   */
  public abstract buildContentSignatures(
    signatures: PdfSignature[] | PdfSignature[][],
    pdfData?: IPdfData,
    options?: IPdfDefinitions,
  ): PdfContent[];

  public getOptionsWithDefault(pdfDefinition: IPdfDefinitions): IPdfOptions {
    return merge({}, this.defaultOptions, pdfDefinition?.options);
  }

  public getTools(
    pdfDefinition: IPdfDefinitionsWithInnerProperty | IPdfDefinitions,
  ): IPdfTools {
    return mergeWith(
      { services: this.services },
      (pdfDefinition as IPdfDefinitionsWithInnerProperty)?.tools,
      (preValue, curValue) => {
        return isNil(curValue) ? preValue : curValue;
      },
    );
  }

  /**
   * build content
   * will call content title/summary/grids/signatures build func
   *
   * @param pdfDefinition pdf define with inner property
   */
  public buildContent(
    pdfDefinition: IPdfDefinitionsWithInnerProperty,
    data: IPdfData,
    additionOptions?: {
      removeLastPageBreak?: boolean;
    },
  ): PdfContent {
    if (isNil(pdfDefinition.content)) {
      return;
    }

    const tools = this.getTools(pdfDefinition);

    let pdfContents = reduce(
      pdfDefinition.content,
      (preContents, content, key) => {
        // not config any content, just return directly
        if (isEmpty(content)) {
          return preContents;
        }

        const buildMethod = isNil(content.builder)
          ? // use capitalize content type to find method with buildContent capitalize(content.type)
            // type def as PdfContentType
            this[`buildContent${upperFirst(camelCase(content.type))}`].bind(
              this,
            )
          : content.builder;

        const valuePath = isNil(content.path) ? key : content.path;

        // need to get child data through value path if isGetChildData is true
        const pdfData = content.isGetChildData
          ? get(data, valuePath, {})
          : data;

        const hiddenThisContent = isFunction(content.hidden)
          ? content.hidden(pdfData, pdfDefinition.options)
          : content.hidden ?? false;

        // hidden this just return pre contents
        if (hiddenThisContent) {
          return preContents;
        }

        const contentSetting = isFunction(content.setting)
          ? content.setting(pdfData, pdfDefinition.options, tools)
          : content.setting;

        // not config any content, just return directly
        if (isEmpty(contentSetting)) {
          return preContents;
        }

        const newContent = buildMethod(
          contentSetting,
          pdfData,
          { ...pdfDefinition, tools },
          key,
        );

        const pageBreakContent = this.buildPageBreak(content.pageBreak);
        const isHavePageBreakContent = !isNil(pageBreakContent);

        // we need concat pre & new contents when new content is array
        if (isArray(newContent)) {
          return concat(
            preContents,
            newContent,
            isHavePageBreakContent ? [pageBreakContent] : [],
          );
        }

        preContents.push(newContent);

        if (isHavePageBreakContent) {
          preContents.push(pageBreakContent);
        }

        return preContents;
      },
      [],
    );

    if (additionOptions?.removeLastPageBreak) {
      pdfContents = this.removePdfContentsLastPageBreak(pdfContents);
    }

    return pdfContents;
  }

  public buildNestedContent(
    pdfDefinition: IPdfDefinitionsWithInnerProperty,
    data: IPdfData,
    content: {
      nestedContent: NestedPdfContent;
      baseOnContent: PdfContent[];
      options: INestedPdfContentOptions;
    },
  ): PdfContent[] {
    const { nestedContent, baseOnContent, options } = content;

    const nestedPdfContent = isFunction(nestedContent)
      ? nestedContent(data, options)
      : nestedContent;

    if (isEmpty(nestedPdfContent)) {
      return baseOnContent;
    }

    const pdfDefinitionWithoutContent = omit(pdfDefinition, 'content');

    const buildPdfContent = (contentSetting: PdfContentSetting): PdfContent =>
      isEmpty(contentSetting)
        ? null
        : this.buildContent(
            merge({}, pdfDefinitionWithoutContent, <PdfContentSetting>{
              content: contentSetting,
            }),
            data,
          );

    const beforeContent = buildPdfContent(nestedPdfContent.before);

    const afterContent = buildPdfContent(nestedPdfContent.after);

    return compact(concat(beforeContent, baseOnContent, afterContent));
  }

  /**
   * use this to build page break pdf content for easily break your pdf
   *
   * @param pageBreak break page config
   */
  public buildPageBreak(
    pageBreak: PdfContentBase['pageBreak'],
  ): ContentText | null | undefined {
    if (isEmpty(pageBreak)) {
      return null;
    }

    return {
      pageBreak,
      text: null,
    };
  }

  public removePdfContentsLastPageBreak(
    pdfContents: ContentText[],
  ): PdfContent[] {
    const lastPdfContent: ContentText = last(pdfContents);

    const isEndWithPageBreak =
      isEqual(['pageBreak', 'text'], keys(lastPdfContent)) &&
      isNil(lastPdfContent.text) &&
      !isNil(lastPdfContent.pageBreak);

    if (isEndWithPageBreak) {
      return dropRight(pdfContents);
    }

    return pdfContents;
  }
}

export { AbstractPdfContentBuilder };
